import "@/shared/clients/sentry.client";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "yeati-ui/style.css";
import "@/styles/index.css";
import { ThemeInit } from "@flowbite-react/init";
import App from "@/app/App";
import { connectAuthTokenSync } from "@/features/auth/services/authTokenSync";
import { authStorageService } from "@/features/auth/services/storage.service";
import { captureCmsPreviewTokenFromUrl } from "@/features/shop/cms/preview/cmsPreviewToken";
import { redirectWwwToApex } from "@/shared/utils/canonicalHost";

// The access token is held in memory only: drop the copy an earlier version
// left in the localStorage, and listen to the tokens the other tabs refresh.
authStorageService.purgeLegacyAuthToken();
connectAuthTokenSync();

// Deep links on the `www.` sub-host miss the origin-scoped session token and
// get bounced to /connexion. Canonicalise to the apex host before the app
// boots so an already authenticated user reaches the intended content (BNF-375).
const isRedirectingToApex = redirectWwwToApex();

// Aperçu d'un brouillon CMS ouvert depuis l'éditeur (`/boutique#apercu=<jeton>`) : le jeton est
// rangé pour l'onglet avant toute redirection de connexion, puis retiré de l'URL. Sur `www.`, on
// attend l'hôte apex (le fragment suit la redirection, `sessionStorage` non).
if (!isRedirectingToApex) captureCmsPreviewTokenFromUrl();

// Recover from stale module preload failures after a deploy. Vite fires
// `vite:preloadError` when a CSS chunk referenced by the old bundle can't be
// loaded; the same condition also breaks dynamic JS imports (handled in
// lazyWithRetry). A hard reload makes the browser pick up the new bundle.
const RELOAD_AT_KEY = "yeati:lazyChunkReloadedAt";
const RELOAD_COOLDOWN_MS = 30_000;

window.addEventListener("vite:preloadError", () => {
  const lastReload = Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
  if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) return;
  window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  window.location.reload();
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeInit />
    <App />
  </StrictMode>,
);
